import React, { useState, useEffect } from 'react';
import {
  faArrowsToCircle,
  faShieldVirus,
  faGaugeHigh,
  faBells,
  faMapLocationDot,
} from '@fortawesome/pro-solid-svg-icons';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import PageHeader from '../pagecomponents/PageHeader';
import profilingScreenshot from '../pagecomponents/screenshots/manager/3. DashBoardOrganization.png';
import placeholderComponent from '../pagecomponents/screenshots/placeholdercomponent.png';

// Nuevas imágenes para las secciones
import organizationImg from '../pagecomponents/screenshots/grow/organization.png';
import dataImg from '../pagecomponents/screenshots/grow/data.png';
import talentPanelImg from '../pagecomponents/screenshots/grow/talentpanel.png';
import alertsImg from '../pagecomponents/screenshots/grow/alerts.png';

import SectionBodyLeft from '../pagecomponents/SectionBodyLeft';
import SectionBodyRight from '../pagecomponents/SectionBodyRight';
import EconomicImpactStats from '../pagecomponents/EconomicImpactStats';
import FAQ from '../pagecomponents/FAQ';

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f9f9f9',
  },
  sectionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '20px',
    padding: '40px 20px',
    backgroundColor: '#fff',
  },
};

function GrowPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    es: {
      hero: {
        title: 'Identifica y mitiga los riesgos de talento con inteligencia',
        intro:
          'La toma de decisiones basada en datos es la base del éxito. Pero si esos datos no se normalizan, acabarás escuchando al manager que más ruido hace. Estandariza la información y decide con confianza.',
        cta: 'Reservar Demo',
      },
      sections: [
        {
          title: 'Dirige tus necesidades empresariales',
          texts: [
            'Unifica los patrones de evaluación para obtener una fotografía real del talento de tu empresa.',
            'Detecta oportunidades de crecimiento tanto en cada equipo como en la organización.',
            'Detecta y analiza la evolución de los datos de talento para reforzar lo que funciona y detener lo que afecta.',
          ],
        },
        {
          title: 'Identifica y mitiga riesgos con datos en tiempo real',
          texts: [
            'Toma decisiones proactivas con información actualizada al instante.',
            'Minimiza el impacto de las rotaciones anticipando el futuro y asegurando la continuidad del negocio.',
            'Monitorea indicadores clave sin demora ni brechas de información.',
          ],
        },
        {
          title: 'TalentPanel®: Visión holística empresarial',
          texts: [
            'Mapa de equipos y roles con indicadores de potencial y riesgo de rotación.',
            'Dashboard visual con métricas clave para tomar las mejores decisiones.',
            'Comparativa de TOP vs. BOTTOM para acciones focalizadas.',
          ],
        },
        {
          title: 'Alertas inteligentes',
          texts: [
            'Recibe notificaciones tempranas cuando se superan los umbrales críticos.',
            'Analiza el impacto potencial en proyectos clave y costes asociados.',
            'Obtén recomendaciones de la IA para intervenciones específicas.',
          ],
        },
      ],
    },
    en: {
      hero: {
        title: 'Identify and Mitigate Talent Risks Intelligently',
        intro:
          'Data-driven decision-making is the foundation of success. But if that data isn’t normalized, you’ll end up listening to the loudest manager. Standardize information and decide with confidence.',
        cta: 'Book Demo',
      },
      sections: [
        {
          title: 'Drive Your Business Needs',
          texts: [
            'Unify evaluation patterns to obtain a true picture of your company’s talent.',
            'Identify growth opportunities within each team and across the organization.',
            'Track and analyze talent data trends to reinforce what works and stop what hinders progress.',
          ],
        },
        {
          title: 'Identify and Mitigate Risks with Real-Time Data',
          texts: [
            'Make proactive decisions with information that updates instantly.',
            'Minimize the impact of turnover by anticipating what’s ahead and ensuring business continuity.',
            'Monitor key indicators without delays or information gaps.',
          ],
        },
        {
          title: 'TalentPanel®: Holistic Enterprise View',
          texts: [
            'Team and role map with indicators of potential and turnover risk.',
            'Visual dashboard with key metrics to support the best decisions.',
            'Top-vs-Bottom comparison for focused actions.',
          ],
        },
        {
          title: 'Smart Alerts',
          texts: [
            'Receive early notifications when critical thresholds are exceeded.',
            'Analyze potential impact on key projects and associated costs.',
            'Get AI-driven recommendations for targeted interventions.',
          ],
        },
      ],
    },
  };

  const lang = content[language] || content.es;
  const handleDemoClick = () => console.log(lang.hero.cta + ' clicked');

  const icons = [
    faArrowsToCircle,
    faShieldVirus,
    faGaugeHigh,
    faBells,
    faMapLocationDot,
  ];

  // Array de imágenes por sección
  const sectionImages = [organizationImg, dataImg, talentPanelImg, alertsImg];

  // Construcción de las secciones con sus imágenes
  const sections = lang.sections.map((sec, idx) => ({
    icon: icons[idx],
    title: sec.title,
    imageSrc: sectionImages[idx] || placeholderComponent,
    texts: sec.texts,
  }));

  const impactStats = [
    {
      number: '44',
      desc:
        language === 'es'
          ? 'Días para cubrir una baja voluntaria'
          : 'Days to fill a replacement',
    },
    {
      number: '35',
      desc:
        language === 'es'
          ? 'Días hasta la productividad desde la contratación'
          : 'Days to productivity from Hiring Day',
    },
    {
      number: '+12K€',
      desc:
        language === 'es'
          ? 'Coste medio por contratación (Directo + Indirecto)'
          : 'Average cost per hiring (Direct + Indirect)',
    },
  ];

  const faqs = {
    es: [
      {
        question:
          '¿Cómo ayuda la IA de la plataforma a identificar y mitigar riesgos de talento en tiempo real?',
        answer:
          'La IA analiza continuamente datos de desempeño, compromiso y rotación para emitir alertas tempranas y recomendar acciones que minimicen el impacto antes de que afecte a los resultados del negocio.',
      },
      {
        question: '¿Qué es TalentPanel® y qué valor aporta a mi organización?',
        answer:
          'TalentPanel® brinda una visión holística de equipos y roles mediante un mapa interactivo y paneles visuales que destacan métricas clave de desempeño y rotación, facilitando decisiones estratégicas basadas en datos.',
      },
      {
        question: '¿Cómo funcionan las Alertas Inteligentes?',
        answer:
          'El sistema envía notificaciones tempranas, cuantifica el impacto potencial y sugiere intervenciones respaldadas por IA.',
      },
    ],
    en: [
      {
        question:
          'How does the platform’s AI help identify and mitigate talent risks in real time?',
        answer:
          'The AI continuously analyzes performance, engagement, and turnover data to issue early alerts and recommend actions that minimize impact before it affects business results.',
      },
      {
        question:
          'What is TalentPanel® and what value does it provide to my organization?',
        answer:
          'TalentPanel® offers a holistic view of teams and roles through an interactive map and visual dashboards that highlight key performance and turnover metrics, enabling data-driven strategic decisions.',
      },
      {
        question: 'How do Smart Alerts work?',
        answer:
          'The system sends early notifications, quantifies potential impact, and suggests AI-backed interventions.',
      },
    ],
  };

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />

      <PageHeader
        title={lang.hero.title}
        subtitle={lang.hero.intro}
        ctaText={lang.hero.cta}
        onCtaClick={handleDemoClick}
        imageSrc={profilingScreenshot}
      />

      <main style={styles.main}>
        <div style={styles.sectionsContainer}>
          {sections.map((sec, i) => {
            // 1ª, 3ª y 5ª sección: izquierda; 2ª y 4ª: derecha
            const Component = [
              SectionBodyLeft,
              SectionBodyRight,
              SectionBodyLeft,
              SectionBodyRight,
              SectionBodyLeft,
            ][i];
            return (
              <Component
                key={i}
                icon={sec.icon}
                title={sec.title}
                imageSrc={sec.imageSrc}
                texts={sec.texts}
              />
            );
          })}
        </div>

        {/* Economic Impact Stats Section */}
        <EconomicImpactStats stats={impactStats} language={language} />

        {/* FAQs Section */}
        <FAQ items={faqs[language]} language={language} />
      </main>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}

export default GrowPage;
